import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface HomeworkAssignment {
  id: string;
  title: string;
  class: string;
  subject: string;
  assignedDate: string;
  dueDate: string;
  status: string;
  totalStudents: number;
  submitted: number;
  pending: number;
  graded: number;
  avgGrade?: string;
}

const TeacherHomeworkHistory = () => {
  const routes = all_routes;
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const classes = [
    "all",
    "10A Mathematics",
    "11B Physics",
    "9C Mathematics",
    "12A Advanced Math",
  ];
  const statuses = ["all", "active", "completed", "overdue"];

  const homeworkData: HomeworkAssignment[] = [
    {
      id: "1",
      title: "Algebra Problems Chapter 5",
      class: "10A Mathematics",
      subject: "Mathematics",
      assignedDate: "2024-08-01",
      dueDate: "2024-08-06",
      status: "completed",
      totalStudents: 32,
      submitted: 32,
      pending: 0,
      graded: 32,
      avgGrade: "B+",
    },
    {
      id: "2",
      title: "Physics Lab Report - Motion",
      class: "11B Physics",
      subject: "Physics",
      assignedDate: "2024-08-03",
      dueDate: "2024-08-10",
      status: "active",
      totalStudents: 28,
      submitted: 25,
      pending: 3,
      graded: 20,
    },
    {
      id: "3",
      title: "Geometry Exercises",
      class: "9C Mathematics",
      subject: "Mathematics",
      assignedDate: "2024-07-28",
      dueDate: "2024-08-02",
      status: "completed",
      totalStudents: 30,
      submitted: 29,
      pending: 1,
      graded: 29,
      avgGrade: "A-",
    },
    {
      id: "4",
      title: "Calculus Problem Set",
      class: "12A Advanced Math",
      subject: "Advanced Mathematics",
      assignedDate: "2024-07-25",
      dueDate: "2024-07-30",
      status: "overdue",
      totalStudents: 25,
      submitted: 23,
      pending: 2,
      graded: 23,
      avgGrade: "B",
    },
    {
      id: "5",
      title: "Trigonometry Applications",
      class: "10A Mathematics",
      subject: "Mathematics",
      assignedDate: "2024-08-04",
      dueDate: "2024-08-08",
      status: "active",
      totalStudents: 32,
      submitted: 28,
      pending: 4,
      graded: 15,
    },
  ];

  const filteredData = homeworkData.filter((hw) => {
    const matchesClass = selectedClass === "all" || hw.class === selectedClass;
    const matchesStatus =
      selectedStatus === "all" || hw.status === selectedStatus;
    return matchesClass && matchesStatus;
  });

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "completed":
        return "success";
      case "active":
        return "primary";
      case "overdue":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case "completed":
        return "ti-check-circle";
      case "active":
        return "ti-clock";
      case "overdue":
        return "ti-alert-triangle";
      default:
        return "ti-file";
    }
  };

  const calculateStats = () => {
    const totalAssignments = filteredData.length;
    const totalStudents = filteredData.reduce(
      (sum, hw) => sum + hw.totalStudents,
      0
    );
    const totalSubmissions = filteredData.reduce(
      (sum, hw) => sum + hw.submitted,
      0
    );
    const avgSubmissionRate =
      totalStudents > 0
        ? Math.round((totalSubmissions / totalStudents) * 100)
        : 0;

    return {
      totalAssignments,
      totalStudents,
      totalSubmissions,
      avgSubmissionRate,
    };
  };

  const stats = calculateStats();

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Homework History</h3>
              <p className="text-muted mb-0">Previously assigned tasks</p>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-6">
                  <label className="form-label">Filter by Class</label>
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    {classes.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls === "all" ? "All Classes" : cls}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Filter by Status</label>
                  <select
                    className="form-select"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status === "all"
                          ? "All Status"
                          : status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <h4 className="text-primary">{stats.totalAssignments}</h4>
                  <p className="mb-0">Total Assignments</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <h4 className="text-info">{stats.totalStudents}</h4>
                  <p className="mb-0">Total Students</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <h4 className="text-success">{stats.totalSubmissions}</h4>
                  <p className="mb-0">Total Submissions</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <h4 className="text-warning">{stats.avgSubmissionRate}%</h4>
                  <p className="mb-0">Avg Submission Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Homework List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Homework Assignments</h5>
            </div>
            <div className="card-body">
              {filteredData.length > 0 ? (
                <div className="row g-3">
                  {filteredData.map((homework) => (
                    <div key={homework.id} className="col-lg-6">
                      <div className="card border">
                        <div className="card-body">
                          <div className="d-flex align-items-start justify-content-between mb-3">
                            <div>
                              <h6 className="mb-1">{homework.title}</h6>
                              <p className="text-muted mb-0 small">
                                {homework.class}
                              </p>
                            </div>
                            <span
                              className={`badge bg-${getStatusColor(
                                homework.status
                              )}`}
                            >
                              <i
                                className={`ti ${getStatusIcon(
                                  homework.status
                                )} me-1`}
                              ></i>
                              {homework.status}
                            </span>
                          </div>

                          <div className="row text-center mb-3">
                            <div className="col-4">
                              <div className="text-info">
                                <strong>{homework.totalStudents}</strong>
                                <p className="mb-0 small text-muted">
                                  Students
                                </p>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="text-success">
                                <strong>{homework.submitted}</strong>
                                <p className="mb-0 small text-muted">
                                  Submitted
                                </p>
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="text-warning">
                                <strong>{homework.pending}</strong>
                                <p className="mb-0 small text-muted">Pending</p>
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="small text-muted">Progress</span>
                              <span className="small">
                                {Math.round(
                                  (homework.submitted /
                                    homework.totalStudents) *
                                    100
                                )}
                                %
                              </span>
                            </div>
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className={`progress-bar bg-${getStatusColor(
                                  homework.status
                                )}`}
                                style={{
                                  width: `${
                                    (homework.submitted /
                                      homework.totalStudents) *
                                    100
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="row text-center">
                              <div className="col-6">
                                <small className="text-muted">Assigned</small>
                                <p className="mb-0 fw-semibold">
                                  {new Date(
                                    homework.assignedDate
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Due Date</small>
                                <p className="mb-0 fw-semibold">
                                  {new Date(
                                    homework.dueDate
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>

                          {homework.avgGrade && (
                            <div className="alert alert-success py-2 mb-3">
                              <small>
                                <strong>Average Grade:</strong>{" "}
                                {homework.avgGrade}
                              </small>
                            </div>
                          )}

                          <div className="d-flex gap-2">
                            <Link
                              to={routes.gradeEntry}
                              className="btn btn-primary btn-sm flex-fill"
                            >
                              <i className="ti ti-eye me-1"></i>View Details
                            </Link>
                            {homework.status === "active" && (
                              <Link
                                to={routes.gradeEntry}
                                className="btn btn-success btn-sm flex-fill"
                              >
                                <i className="ti ti-edit me-1"></i>Grade
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                    <i className="ti ti-book-off fs-1 text-secondary"></i>
                  </div>
                  <h5>No homework assignments found</h5>
                  <p className="text-muted">
                    No assignments match your selected filters.
                  </p>
                  <Link to={routes.manageHomework} className="btn btn-primary">
                    <i className="ti ti-plus me-2"></i>Create Assignment
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0">Quick Actions</h6>
            </div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.manageHomework}
                    className="btn btn-warning w-100"
                  >
                    <i className="ti ti-plus me-2"></i>New Assignment
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.gradeEntry}
                    className="btn btn-success w-100"
                  >
                    <i className="ti ti-edit me-2"></i>Grade Papers
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link to={routes.teacherTasks} className="btn btn-info w-100">
                    <i className="ti ti-clipboard-list me-2"></i>View Tasks
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.teacherDashboard}
                    className="btn btn-outline-secondary w-100"
                  >
                    <i className="ti ti-home me-2"></i>Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherHomeworkHistory;
